import React, {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { type RootState } from '~redux/rootReducer'
import { setSelectedEIOrganization } from '~redux/slices/organizationSlice'
import { type AppDispatch } from '~redux/store'
import { type EmailIdentity } from '~types'

export interface AISettingsContextType {
  selectedOrganization: EmailIdentity | null
  setSelectedOrganization: (organization: EmailIdentity) => void
}

const AISettingsContext = createContext<AISettingsContextType | undefined>(undefined)

const useAISettingsValue = (): AISettingsContextType => {
  const dispatch: AppDispatch = useDispatch()
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )

  const setSelectedOrganization = useCallback(
    (organization: EmailIdentity) => {
      if (organization.id !== selectedOrganization?.id) {
        dispatch(setSelectedEIOrganization(organization))
      }
    },
    [dispatch, selectedOrganization],
  )

  const memoizedSelectedOrganization = useMemo(() => selectedOrganization, [selectedOrganization])

  return useMemo(
    () => ({
      selectedOrganization: memoizedSelectedOrganization ?? null,
      setSelectedOrganization,
    }),
    [memoizedSelectedOrganization, setSelectedOrganization],
  )
}

// Shared AI settings components (e.g. AIInteractionLogContent) are also rendered outside
// AISettingsScreen, so fall back to the same Redux-backed value when there is no provider.
export const useAISettings = (): AISettingsContextType => {
  const context = useContext(AISettingsContext)
  const fallback = useAISettingsValue()
  return context ?? fallback
}

interface AISettingsProviderProps {
  children: ReactNode
}

export const AISettingsProvider = ({ children }: AISettingsProviderProps): ReactElement => {
  const value = useAISettingsValue()
  return <AISettingsContext.Provider value={value}>{children}</AISettingsContext.Provider>
}
